import { useState } from 'react';
import type { ThreadStatus, ThreadMessage } from './types';
import { Button, ImageViewer, formatDateTime } from '../components';
import './ThreadConversation.css';

const EDIT_WINDOW_MS = 24 * 60 * 60 * 1000;
const LOCKED_STATUSES: ThreadStatus[] = ['resolved', 'wont_fix', 'closed'];

interface ThreadConversationProps {
    description: string;
    creatorName?: string;
    creatorRole?: string;
    createdAt: string;
    descriptionEditedAt?: string;
    screenshotUrls?: string[];
    messages: ThreadMessage[];
    onSendMessage: (text: string) => Promise<void>;
    canReply: boolean;
    replyPlaceholder?: string;
    onEditDescription?: () => void;
    currentUserId?: string;
    status?: ThreadStatus;
    onEditMessage?: (messageId: string, text: string) => Promise<void>;
    /** Optional extra content rendered above the description (e.g., help query context). */
    renderContext?: () => React.ReactNode;
    /** Extra actions rendered alongside the Reply button (e.g. Mark Resolved). */
    replyExtraActions?: React.ReactNode;
    /** Always render the bottom action row even if canReply is false (so extra actions stay visible). */
    showActionsWhenNotReplying?: boolean;
    /** Size of the Reply button. Default 'md'. Use 'sm' inside dialogs/modals. */
    replyButtonSize?: 'sm' | 'md' | 'lg';
    /** Tag replies that were also sent as an in-app pop-up (admin view only). */
    markPopupMessages?: boolean;
}

const formatDate = formatDateTime;

export function ThreadConversation({
    description,
    creatorName,
    creatorRole,
    createdAt,
    descriptionEditedAt,
    screenshotUrls,
    messages,
    onSendMessage,
    canReply,
    replyPlaceholder = 'Write a reply...',
    onEditDescription,
    currentUserId,
    status,
    onEditMessage,
    renderContext,
    replyExtraActions,
    showActionsWhenNotReplying = false,
    replyButtonSize = 'md',
    markPopupMessages = false,
}: ThreadConversationProps) {
    const [reply, setReply] = useState('');
    const [sending, setSending] = useState(false);

    const handleSend = async () => {
        if (!reply.trim()) return;
        setSending(true);
        try {
            await onSendMessage(reply.trim());
            setReply('');
        } finally {
            setSending(false);
        }
    };

    const statusLocked = status ? LOCKED_STATUSES.includes(status) : false;

    const canEditMessage = (msg: ThreadMessage): boolean => {
        if (!currentUserId || !onEditMessage) return false;
        if (msg.senderId !== currentUserId) return false;
        if (statusLocked) return false;
        return Date.now() - Date.parse(msg.createdAt) < EDIT_WINDOW_MS;
    };

    return (
        <div className="thread-conversation">
            {renderContext?.()}

            {/* Original description as first message. Empty on a thread a teacher
                opened — their words are the first message, and this slot is the
                creator's (the student's). */}
            {description && <div className="thread-msg thread-msg-creator">
                <div className="thread-msg-header">
                    <span className="thread-sender">{creatorName}</span>
                    <span className="thread-role">{creatorRole}</span>
                    <span className="thread-time">
                        {formatDate(createdAt)}
                        {descriptionEditedAt && <span className="thread-edited"> · edited</span>}
                    </span>
                    {onEditDescription && (
                        <button type="button" className="thread-edit-btn" onClick={onEditDescription}>
                            Edit
                        </button>
                    )}
                </div>
                <p className="thread-msg-body">{description}</p>
                {screenshotUrls && screenshotUrls.length > 0 && (
                    <div className="thread-screenshots">
                        {screenshotUrls.map((url) => (
                            <ImageViewer key={url} src={url} alt="" className="thread-screenshot" />
                        ))}
                    </div>
                )}
            </div>}

            {/* Threaded messages */}
            {messages.map((msg) => (
                <ThreadMessageRow
                    key={msg.id}
                    msg={msg}
                    editable={canEditMessage(msg)}
                    onSaveEdit={onEditMessage}
                    markPopup={markPopupMessages}
                />
            ))}

            {/* Reply input */}
            {canReply ? (
                <div className="thread-reply">
                    <textarea
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        placeholder={replyPlaceholder}
                        rows={3}
                        maxLength={5000}
                    />
                    <div className="thread-reply-actions">
                        {replyExtraActions}
                        <Button onClick={handleSend} size={replyButtonSize} disabled={sending || !reply.trim()}>
                            {sending ? 'Sending...' : 'Reply'}
                        </Button>
                    </div>
                </div>
            ) : (showActionsWhenNotReplying && replyExtraActions) ? (
                <div className="thread-reply-actions thread-reply-actions--standalone">
                    {replyExtraActions}
                </div>
            ) : null}
        </div>
    );
}

function ThreadMessageRow({
    msg,
    editable,
    onSaveEdit,
    markPopup,
}: {
    msg: ThreadMessage;
    editable: boolean;
    onSaveEdit?: (messageId: string, text: string) => Promise<void>;
    markPopup: boolean;
}) {
    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState(msg.text);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const beginEdit = () => { setDraft(msg.text); setError(''); setIsEditing(true); };
    const cancel = () => { setIsEditing(false); setError(''); };

    const save = async () => {
        if (!onSaveEdit) return;
        const next = draft.trim();
        if (!next || next === msg.text) { setIsEditing(false); return; }
        setSaving(true);
        setError('');
        try {
            await onSaveEdit(msg.id, next);
            setIsEditing(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const roleClass = msg.senderRole === 'teacher' ? 'thread-msg-recipient' : 'thread-msg-creator';

    return (
        <div className={`thread-msg ${roleClass}`}>
            <div className="thread-msg-header">
                <span className="thread-sender">{msg.senderName}</span>
                <span className="thread-role">{msg.senderRole}</span>
                <span className="thread-time">
                    {formatDate(msg.createdAt)}
                    {msg.editedAt && <span className="thread-edited"> · edited</span>}
                    {markPopup && msg.showAsPopup && <span className="thread-edited" title="Also shown to the submitter as an in-app pop-up"> · sent as pop-up</span>}
                </span>
                {editable && !isEditing && (
                    <button type="button" className="thread-edit-btn" onClick={beginEdit}>Edit</button>
                )}
            </div>

            {isEditing ? (
                <div className="thread-edit">
                    <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} maxLength={5000} disabled={saving} autoFocus />
                    {error && <p className="thread-edit-error">{error}</p>}
                    <div className="thread-edit-actions">
                        <Button type="button" onClick={cancel} disabled={saving}>Cancel</Button>
                        <Button type="button" onClick={save} disabled={saving || !draft.trim() || draft.trim() === msg.text}>
                            {saving ? 'Saving...' : 'Save'}
                        </Button>
                    </div>
                </div>
            ) : (
                <>
                    <p className="thread-msg-body">{msg.text}</p>
                    {msg.attachmentUrls && msg.attachmentUrls.length > 0 && (
                        <div className="thread-screenshots">
                            {msg.attachmentUrls.map((url) => (
                                <ImageViewer key={url} src={url} alt="" className="thread-screenshot" />
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
